/* import __COLOCATED_TEMPLATE__ from './empty-state.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import type Template from 'embercom/models/template';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import { tracked } from '@glimmer/tracking';
import { debounce } from '@ember/runloop';
import { modifier } from 'ember-modifier';

interface Args {
  startFromScratchTemplate: Template;
  templates: Template[];
  createNewTask: () => void;
  selectTemplate: (template: Template, showBackButton: boolean) => void;
  transitionToPreview: () => void;
  toggleTemplateModal: () => void;
}

const RESIZE_DEBOUNCE_MS = 150;
const CARD_WIDTH = 206;
const CARD_GAP = 16;
const MIN_COLUMNS = 2;
const MAX_COLUMNS = 6;

export default class EmptyState extends Component<Args> {
  @service declare intl: IntlService;
  @service declare intercomEventService: any;
  @tracked columnCount = 2;
  resizeObserver?: ResizeObserver;

  calculateColumns(width: number): number {
    let availableWidth = width - CARD_GAP;
    let columnsWithGap = Math.floor(availableWidth / (CARD_WIDTH + CARD_GAP));
    return Math.max(MIN_COLUMNS, Math.min(MAX_COLUMNS, columnsWithGap));
  }

  updateColumnCount = (width: number) => {
    this.columnCount = this.calculateColumns(width);
  };

  debouncedUpdateColumnCount = (width: number) => {
    debounce(this, this.updateColumnCount, width, RESIZE_DEBOUNCE_MS);
  };

  setupObserver = modifier(
    (element: HTMLElement, _positional: unknown[], _named: Record<string, unknown>) => {
      this.updateColumnCount(element.offsetWidth);

      this.resizeObserver = new ResizeObserver((entries) => {
        for (let entry of entries) {
          if (entry.contentRect) {
            this.debouncedUpdateColumnCount(entry.contentRect.width);
          }
        }
      });

      this.resizeObserver.observe(element);

      return () => {
        this.resizeObserver?.disconnect();
      };
    },
    { eager: false },
  );

  get visibleTemplates() {
    let maxTemplates = this.columnCount - 1;
    return (this.args.templates || []).slice(0, maxTemplates);
  }

  get isTemplatesReady() {
    return this.visibleTemplates.length > 0;
  }

  recordAnalyticsEvent(event: string) {
    this.intercomEventService.trackAnalyticsEvent({
      action: event,
      object: 'task',
      place: 'standalone_tasks',
    });
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Tasks::EmptyState': typeof EmptyState;
    'standalone/tasks/empty-state': typeof EmptyState;
  }
}
